import { InfoOutline } from '@mui/icons-material';
import {
  Box,
  Breakpoint,
  styled,
  Theme,
  Tooltip,
  TooltipProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { CSSProperties, FC, PropsWithChildren, ReactNode } from 'react';
import Dot from '../Dot';
import { Column, Row } from '../Layout';

type FormFieldSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

const sizesMap: Record<FormFieldSize, string> = {
  xxs: '80px',
  xs: '120px',
  sm: '220px',
  md: '400px',
  lg: '500px',
  xl: '600px',
  xxl: '700px',
};

const labelSizesMap: Record<FormFieldSize, string> = {
  xxs: '100px',
  xs: '150px',
  sm: '210px',
  md: '290px',
  lg: '320px',
  xl: '400px',
  xxl: '450px',
};

const ColumnWrapper = styled(Column, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size?: FormFieldSize }>(({ size }) => ({
  width: '100%',
  maxWidth: size ? sizesMap[size] : '100%',
}));

const RowWrapper = styled(Row, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size?: FormFieldSize }>(({ size }) => ({
  width: '100%',
  maxWidth: size ? sizesMap[size] : '100%',
}));

const LabelColumn = styled(Column, {
  shouldForwardProp: (prop) => prop !== 'labelSize' && prop !== 'isHorizontal',
})<{ labelSize?: FormFieldSize; isHorizontal?: boolean }>(
  ({ theme, labelSize, isHorizontal }) => ({
    flex:
      isHorizontal && labelSize
        ? `0 0 ${labelSizesMap[labelSize]}`
        : '0 0 auto',
    ...(isHorizontal && {
      paddingTop: theme.spacing(theme.spacingSizes.xs),
    }),
  }),
);

const ChildrenWrapper = styled(Box, {
  shouldForwardProp: (prop: string) =>
    prop !== 'size' && prop !== 'justifyChildren',
})<{ size?: FormFieldSize; justifyChildren?: CSSProperties['justifyContent'] }>(
  ({ size, justifyChildren }) => ({
    width: '100%',
    display: 'flex',
    ...(justifyChildren && { justifyContent: justifyChildren }),
    ...(size && { maxWidth: sizesMap[size] }),
  }),
);

type Props = {
  label?: string | ReactNode;
  tooltip?: ReactNode;
  extraInformation?: string;
  size?: FormFieldSize;
  labelColorLight?: boolean;
  spacing?: keyof Theme['spacingSizes'];
  dotColor?: string;
  dotTooltipText?: string;
  labelSize?: FormFieldSize;
  breakpoint?: Breakpoint;
  align?: 'flex-start' | 'center' | 'flex-end';
  justifyChildren?: CSSProperties['justifyContent'];
  tooltipSlotProps?: TooltipProps['slotProps'];
};

const HorizontalFormField: FC<PropsWithChildren<Props>> = ({
  children,
  label,
  extraInformation,
  size,
  labelColorLight,
  tooltip,
  spacing = 'sm',
  dotColor,
  dotTooltipText,
  labelSize,
  breakpoint,
  align = 'center',
  justifyChildren,
  tooltipSlotProps,
}) => {
  const theme = useTheme();
  const isCollapsed =
    useMediaQuery(theme.breakpoints.down(breakpoint ?? 'xs')) && !!breakpoint;
  const Wrapper = isCollapsed ? ColumnWrapper : RowWrapper;
  return (
    <Wrapper
      size={size}
      spacing={spacing}
      align={isCollapsed ? undefined : align}
    >
      {!!label &&
        (typeof label === 'string' ? (
          <LabelColumn
            spacing="xs"
            labelSize={labelSize}
            isHorizontal={!isCollapsed}
          >
            <Row centered spacing="xxs">
              <Typography
                color={labelColorLight ? 'grey.600' : undefined}
                variant="h6"
              >
                {label}
              </Typography>
              {tooltip ? (
                <Tooltip
                  title={tooltip}
                  slotProps={{
                    popper: { disablePortal: true },
                    ...tooltipSlotProps,
                  }}
                >
                  <InfoOutline fontSize="inherit" />
                </Tooltip>
              ) : null}
              {dotColor ? (
                <Tooltip
                  title={dotTooltipText}
                  slotProps={{ popper: { disablePortal: true } }}
                >
                  <Dot color={dotColor} showMargin />
                </Tooltip>
              ) : null}
            </Row>
            {!!extraInformation && (
              <Typography variant="body2">{extraInformation}</Typography>
            )}
          </LabelColumn>
        ) : (
          label
        ))}

      <ChildrenWrapper
        size={size}
        justifyChildren={isCollapsed ? undefined : justifyChildren}
      >
        {children}
      </ChildrenWrapper>
    </Wrapper>
  );
};

export default HorizontalFormField;
